import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useKey } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { laptopAndAbove } from 'style/mediaQuery';

// Don't slow down tests
const VIDEO_TIMEOUT = process.env.NODE_ENV === 'test' ? 10 : 3000;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-3%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const Root = styled(Vertical).attrs({ gap: 0 })`
  height: var(--100vh);
  background: var(--background, var(--c-neutral-100));
  background-size: cover;

  &.withRightBar {
    background-position: center 80%;
    background-size: contain;
    background-repeat: no-repeat;

    @media ${laptopAndAbove} {
      background-position-y: 12vh;
    }
  }
`;
const Wrapper = styled.div`
  --bench-width: 100vw;
  display: flex;
  flex: 1;
  background: transparent;
  isolation: isolate;
  flex-direction: column;
  @media ${laptopAndAbove} {
    --bench-width: 420px;
    flex-direction: row;
    overflow: hidden;
  }
  .withRightBar & {
    @media ${laptopAndAbove} {
      --bench-width: 390px;
      display: grid;
      grid-template-columns: var(--bench-width) 1fr var(--bench-width);
    }
  }
`;
const LineupPicker = styled.aside`
  z-index: 1;
  @media ${laptopAndAbove} {
    border-right: 1px solid var(--c-neutral-200);
    background: var(--c-neutral-100);
  }
`;
const SideBarWrapper = styled.div`
  overflow: hidden;
  width: min-content;
  &:not(.right) {
    border-top-right-radius: var(--double-unit);
  }
  &.right {
    /** TODO gbreux iterate to display rules on mobile properly with new layout */
    display: none;
    @media ${laptopAndAbove} {
      width: 100%;
      display: initial;
      border-top-left-radius: var(--double-unit);
      margin-left: auto;
    }
  }
`;

const LineupPickerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  background: var(--c-neutral-100);
  @media ${laptopAndAbove} {
    opacity: 0;
    animation: ${slideIn} 1s ease-in-out forwards var(--delay, 0.4s);
    height: 100%;
  }
`;
const MainWrapper = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const LineupHeader = styled.header`
  @media ${laptopAndAbove} {
    display: block;
    width: 100%;
    opacity: 0;
    animation: ${fadeIn} 1s ease-in-out forwards var(--delay, 1s);
    color: var(--c-static-neutral-100);
  }
`;
const LineupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex: 1;
  @media ${laptopAndAbove} {
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const Lineup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  isolation: isolate;
  flex: 1;
  background: var(--c-static-neutral-1000);
  > * {
    z-index: 2;
  }
  @media ${laptopAndAbove} {
    &:before {
      position: absolute;
      inset: 0;
      content: '';
      background: linear-gradient(
          to left,
          rgba(var(--c-static-rgb-neutral-1000), 0.8) 0%,
          rgba(var(--c-static-rgb-neutral-1000), 0) 20%
        ),
        linear-gradient(
          to right,
          rgba(var(--c-static-rgb-neutral-1000), 0.8) 0%,
          rgba(var(--c-static-rgb-neutral-1000), 0) 20%
        );
      pointer-events: none;
    }
  }
`;
const Video = styled.video`
  position: absolute;
  z-index: -1;
  inset: 0;
  object-fit: cover;
  object-position: center bottom;
  height: 100%;
  width: 100%;
`;
type Props = {
  video: string;
  poster: string;
  lineupHeader?: ReactNode;
  bench: ReactNode;
  lineup: ReactNode;
  stall?: boolean;
  disableAnimation?: boolean;
  videoDuration?: string;
  rightBar?: ReactNode;
};

export const ComposeTeamLayout = ({
  video,
  poster,
  lineupHeader,
  bench,
  lineup,
  stall,
  disableAnimation = false,
  videoDuration = '3s',
  rightBar,
}: Props) => {
  const [searchParams] = useSearchParams();
  const isComingFromWebview = !!searchParams.get('deeplink');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ready, setReady] = useState(disableAnimation || isComingFromWebview);
  const [fallback, setFallback] = useState(
    disableAnimation || isComingFromWebview
  );
  const [removeDelay, setRemoveDelay] = useState(
    disableAnimation || isComingFromWebview
  );

  useKey('Escape', () => {
    setRemoveDelay(true);
    setFallback(true);
    setReady(true);
  });

  useEffect(() => {
    if (stall || rightBar) {
      return undefined;
    }
    const currentRef = videoRef.current;
    const to = setTimeout(() => {
      // make sure the field load if the video takes too much time to appear
      setReady(true);
      setFallback(true);
    }, VIDEO_TIMEOUT);
    const handler = () => {
      setFallback(false);
      setReady(true);
      clearTimeout(to);
    };
    if (currentRef) {
      // Safari will not call canplaythrough in Energy Saver Mode
      currentRef.addEventListener('canplaythrough', handler);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('canplaythrough', handler);
      }
      clearTimeout(to);
    };
  }, [stall, rightBar]);

  if (rightBar) {
    return (
      <Root
        className="withRightBar"
        style={
          { '--background': rightBar ? `url(${poster})` : '' } as CSSProperties
        }
      >
        <LineupHeader style={{ '--delay': '0s' } as any}>
          {lineupHeader}
        </LineupHeader>
        <Wrapper>
          <SideBarWrapper>{bench}</SideBarWrapper>
          {lineup}
          <SideBarWrapper className="right">{rightBar}</SideBarWrapper>
        </Wrapper>
      </Root>
    );
  }

  return (
    <Root>
      {!stall && (
        <Wrapper>
          <LineupPicker>
            <LineupPickerContent
              style={{ '--delay': removeDelay ? '0s' : '0.5s' } as any}
            >
              {bench}
            </LineupPickerContent>
          </LineupPicker>
          <MainWrapper>
            <LineupHeader
              style={{ '--delay': removeDelay ? '0s' : '0.5s' } as any}
            >
              {lineupHeader}
            </LineupHeader>
            <LineupWrapper>
              <Lineup
                style={
                  {
                    '--delay': removeDelay
                      ? '0s'
                      : `calc(${videoDuration} - 0.5s)`,
                  } as any
                }
              >
                {fallback ? (
                  <Video as="img" src={poster} alt="" />
                ) : (
                  <Video
                    ref={videoRef}
                    muted
                    autoPlay
                    playsInline
                    // 1x1 transparent GIF to avoid ugly default poster in Android webview
                    poster="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                  >
                    <source src={video} />
                  </Video>
                )}
                {ready && lineup}
              </Lineup>
            </LineupWrapper>
          </MainWrapper>
        </Wrapper>
      )}
    </Root>
  );
};
